/**
 * Agents API slice.
 * Leaf module (Perf 3.1.b/e Step 5). Pulls only the shared chokepoint from
 * ./client; never imports a sibling @/lib/api slice.
 */
import { Platform } from 'react-native';
import { fetchWithAuth, ApiError, macosUploadFile, avatarUriToFile } from './client';

function normalizeAgentPagination(pagination = {}, page = 1, fallbackPerPage = 20) {
  const current = pagination.current || pagination.page || page;
  const perPage = pagination.perPage || fallbackPerPage;
  const total = pagination.total || 0;
  const pages = pagination.pages || Math.ceil(total / perPage) || 1;
  return {
    current,
    page: current,
    pages,
    total,
    perPage,
    hasMore: pagination.hasMore != null ? !!pagination.hasMore : total > current * perPage,
  };
}

export const agentsApi = {
  getConversationPreviews: async (botIds = []) => {
    const ids = [...new Set(botIds.flatMap(value => {
      const id = typeof value === 'string' ? value.trim() : '';
      return id ? [id] : [];
    }))].sort().slice(0, 100);
    if (!ids.length) return { previewsByBotId: {} };
    return fetchWithAuth(`/json/agents/conversations?ids=${encodeURIComponent(ids.join(','))}`);
  },

  create: async (formData) => {
    return fetchWithAuth('/json/agents/new', {
      method: 'POST',
      body: formData,
    });
  },

  saveWithAvatar: async ({ agentId, avatar, fields }) => {
    const endpoint = agentId ? `/json/agents/${agentId}` : '/json/agents/new';
    const method = agentId ? 'PUT' : 'POST';
    const avatarType = avatar?.type || 'image/png';
    const avatarName = avatar?.name || 'avatar.png';
    const localUri = avatar?.uri && !avatar.uri.startsWith('http') ? avatar.uri : null;

    // Prompt-only saves stay JSON. Multipart FormData turns each textarea
    // newline into CRLF, which used to push a 3990-character prompt over 4000.
    if (!localUri) {
      return fetchWithAuth(endpoint, {
        method,
        body: {
          ...fields,
          ...(avatar?.uri?.startsWith('http') ? { avatar_url: avatar.uri } : {}),
        },
      });
    }

    if (Platform.OS === 'macos') {
      return macosUploadFile(localUri, {
        fieldName: 'avatar',
        fileName: avatarName,
        mimeType: avatarType,
        endpoint,
        method,
        extraFields: fields,
      });
    }

    const formData = new FormData();
    for (const [key, value] of Object.entries(fields)) {
      formData.append(key, value);
    }

    if (Platform.OS === 'web') {
      const file = await avatarUriToFile(localUri, avatarType, avatarName);
      formData.append('avatar', file);
    } else {
      formData.append('avatar', {
        uri: localUri,
        type: avatarType,
        name: avatarName,
      });
    }

    return fetchWithAuth(endpoint, { method, body: formData });
  },

  generateContent: async ({ name, description, generate, modelType, onboardingId }) => {
    try {
      const data = await fetchWithAuth('/json/agents/my/generate', {
        method: 'POST',
        timeoutMs: 180000,
        networkRetries: 0,
        body: {
          name,
          ...(onboardingId ? { onboardingId } : {}),
          ...(description != null && description !== '' ? { description } : {}),
          generate,
          modelType
        }
      });

      if (data.error) {
        throw new ApiError(data.message || `Failed to generate ${generate}`, 400, data);
      }

      return generate === 'avatar' && onboardingId ? { url: data.generated, avatarGenerations: data.avatarGenerations } : data.generated;
    } catch (error) {
      console.error(`Error generating ${generate}:`, error);
      throw error;
    }
  },

  getRecentAgents: async () => {
    try {
      const data = await fetchWithAuth('/json/agents/recent');
      
      if (data.error) {
        throw new ApiError(data.message || 'Failed to fetch recent agents', 400, data);
      }
      
      return data.agents;
    } catch (error) {
      console.error('Error fetching recent agents:', error);
      throw error;
    }
  },

  getFeaturedOfficialAgents: async () => {
    try {
      const data = { agents: [] }; //await fetchWithAuth('/json/agents/official/featured');
      
      if (data.error) {
        throw new ApiError(data.message || 'Failed to fetch featured official agents', 400, data);
      }
      
      return data.agents;
    } catch (error) {
      console.error('Error fetching featured official agents:', error);
      throw error;
    }
  },

  update: async (agentId, formData) => {
    return fetchWithAuth(`/json/agents/${agentId}`, {
      method: 'PUT',
      body: formData,
    });
  },

  getMyAgents: async (page = 1, limit = 50) => {
    const data = await fetchWithAuth(`/json/agents/my?page=${page}&limit=${limit}`);
    return {
      ...data,
      pagination: normalizeAgentPagination(data.pagination, page, limit),
    };
  },

  search: async (query, page = 1) => {
    return fetchWithAuth(`/json/agents/search?q=${encodeURIComponent(query)}&page=${page}`);
  },

  getByCategory: async (slug, page = 1) => {
    const data = await fetchWithAuth(`/json/agent-categories/${slug}/agents?page=${page}`);
    return {
      ...data,
      pagination: normalizeAgentPagination(data.pagination, page),
    };
  },

  getCategories: async (page = 1, limit = 10) => {
    return fetchWithAuth(`/json/agent-categories-with-agents?page=${page}&limit=${limit}`);
  },

  favoriteAgent: async (agentId) => {
    const data = await fetchWithAuth(`/json/agents/${agentId}/favorite`, {
      method: 'POST'
    });
    const success = !data.error;
    return success;
  },

  unfavoriteAgent: async (agentId) => {
    const data = await fetchWithAuth(`/json/agents/${agentId}/favorite`, {
      method: 'DELETE'
    });
    const success = !data.error;
    return success;
  },

  getAllCategories: async () => {
    return fetchWithAuth('/json/agent-categories');
  },

  getAgentById: async (agentId) => {
    try {
      const data = await fetchWithAuth(`/json/agents/${agentId}`);
      
      if (data.error) {
        throw new Error(data.message);
      }
      
      return data.agent;
    } catch (error) {
      console.error('Error fetching agent:', error);
      throw error;
    }
  },

  deleteAgent: async (agentId) => {
    try {
      const response = await fetchWithAuth(`/json/agents/${agentId}`, {
        method: 'DELETE'
      });

      if (response.error) {
        throw new Error(response.message || 'Failed to delete agent');
      }

      return response;
    } catch (error) {
      console.error('Error deleting agent:', error);
      throw error;
    }
  },
};
